import { useEffect, useRef, useState } from 'react'
import { FileText, ChevronDown, ChevronUp } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import type { Message, Participant, Artifact } from '../../types'
import { AGENT_COLORS, AGENT_DISPLAY_NAMES } from '../../types'

interface MessageListProps {
  messages: Message[]
  participants: Participant[]
  artifacts: Artifact[]
  currentParticipantId: string
}

function getParticipant(participants: Participant[], id: string) {
  return participants.find(p => p.id === id)
}

function ArtifactCard({ artifact }: { artifact: Artifact }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="mx-auto max-w-md my-2">
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="w-full card p-3 flex items-center gap-3 hover:bg-paper transition-colors text-left cursor-pointer"
      >
        <div className="w-9 h-9 bg-paper border-2 border-navy flex items-center justify-center shrink-0">
          <FileText className="w-4 h-4 text-navy" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-navy truncate">{artifact.fileName}</p>
          <p className="mono-label text-navy/70">
            {artifact.uploadedBy} &middot; {artifact.wordCount.toLocaleString()} words
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-navy/70 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-navy/70 shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="mt-1 card border-t-0 p-3 text-sm text-navy/80 max-h-60 overflow-y-auto whitespace-pre-wrap">
          {artifact.content.startsWith('image:') ? (
            <img
              src={`data:${artifact.content.slice(6).replace(':', ';base64,')}`}
              alt={artifact.fileName}
              className="max-w-full"
            />
          ) : (
            artifact.content
          )}
        </div>
      )}
    </div>
  )
}

export default function MessageList({
  messages,
  participants,
  artifacts,
  currentParticipantId,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)

  // Streaming agent replies grow inside an existing message doc, so keying the scroll
  // on the message count alone would leave the tail of a long reply off-screen.
  const contentLength = messages.reduce((total, m) => total + (m.content?.length || 0), 0)

  useEffect(() => {
    if (autoScroll) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages.length, contentLength, autoScroll])

  function handleScroll() {
    const el = containerRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    setAutoScroll(atBottom)
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
    >
      {messages.map((msg) => {
        // Reflection messages: only show if visible
        if (msg.type === 'reflection' && !msg.visible) return null

        // System messages: a ruled margin note, not a floating pill
        if (msg.type === 'system') {
          return (
            <div key={msg.id} className="flex items-center gap-3 my-4">
              <span className="h-px flex-1 bg-navy/25" />
              <span className="mono-label text-navy/70 text-center">{msg.content}</span>
              <span className="h-px flex-1 bg-navy/25" />
            </div>
          )
        }

        // Artifact messages
        if (msg.type === 'artifact') {
          const artifact = artifacts.find(a => a.fileName === msg.content || a.id === msg.content)
          if (artifact) return <ArtifactCard key={msg.id} artifact={artifact} />
          return (
            <div key={msg.id} className="flex justify-center my-2">
              <span className="mono-label text-navy/70">Artifact: {msg.content}</span>
            </div>
          )
        }

        // Agent messages
        if (msg.agentName) {
          // A streaming doc can exist for a moment before its first text lands.
          // The writing indicator covers that gap, so skip the empty bubble.
          if (msg.streaming && !msg.content?.trim()) return null

          const color = AGENT_COLORS[msg.agentName]
          const name = AGENT_DISPLAY_NAMES[msg.agentName]
          return (
            <div key={msg.id} className="flex items-start gap-2.5 max-w-[85%]">
              <div
                className="w-8 h-8 border-2 border-navy flex items-center justify-center shrink-0 font-mono text-xs font-semibold text-white"
                style={{ backgroundColor: color }}
                aria-hidden="true"
              >
                {name.charAt(0)}
              </div>
              <div className="min-w-0">
                <p className="mono-label text-xs font-semibold mb-1" style={{ color }}>
                  {name}
                </p>
                <div data-testid="agent-message"
                  className={`card px-3.5 py-2.5 text-base leading-relaxed prose prose-base max-w-[68ch] prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-2 prose-headings:font-display prose-strong:text-inherit prose-a:text-teal prose-blockquote:border-l-2 prose-blockquote:border-l-amber prose-blockquote:bg-paper prose-blockquote:py-1 prose-blockquote:not-italic prose-code:font-mono${
                    msg.streaming ? ' streaming' : ''
                  }`}
                >
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
              </div>
            </div>
          )
        }

        // User messages
        const isMe = msg.participantId === currentParticipantId
        const participant = getParticipant(participants, msg.participantId)
        const userColor = participant?.color || '#115e59'

        if (isMe) {
          return (
            <div key={msg.id} className="flex justify-end">
              <div className="max-w-[75%]">
                <div className="bg-navy text-stone border-2 border-navy px-3.5 py-2.5 text-base leading-relaxed whitespace-pre-wrap">
                  {msg.content}
                </div>
              </div>
            </div>
          )
        }

        return (
          <div key={msg.id} className="flex items-start gap-2.5 max-w-[85%]">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0 border-2 border-navy"
              style={{ backgroundColor: userColor }}
              aria-hidden="true"
            >
              {msg.displayName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="mono-label text-navy/70 mb-1">{msg.displayName}</p>
              <div className="card px-3.5 py-2.5 text-base leading-relaxed whitespace-pre-wrap">
                {msg.content}
              </div>
            </div>
          </div>
        )
      })}
      <div ref={bottomRef} />
    </div>
  )
}
